














































































































































































































































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import { Schedule, SendScheduleSMSResponse } from "@/api";

interface EmployeeRow {
  employeeId: number;
  fullName: string;
  profilePicture?: string;
  schedules: Schedule[];
}

// selectedSchedules[employeeId] = Set of schedule IDs
type SelectionMap = Record<number, Set<number>>;

@Component
export default class SendScheduleSMSModal extends Vue {
  @Prop({ default: false }) value!: boolean;

  @Prop({ default: () => [] }) employees!: Array<{
    id: number;
    firstName: string;
    lastName: string;
    profilePicture?: string;
    [key: string]: unknown;
  }>;

  @Prop({ default: () => [] }) schedules!: Schedule[];

  internalValue = this.value;
  /** which employees are expanded to show their schedules */
  expandedIds: number[] = [];
  /** nested selection: employeeId -> Set of selected schedule IDs */
  selectedSchedules: SelectionMap = {};
  search = "";
  sending = false;
  results: {
    successCount: number;
    failureCount: number;
    errors: string[];
  } | null = null;

  // ─── Watchers ───────────────────────────────────────────────────────────────

  @Watch("value")
  onValueChange(val: boolean) {
    this.internalValue = val;
    if (val) {
      this.initSelection();
    }
  }

  @Watch("internalValue")
  onInternalValueChange(val: boolean) {
    this.$emit("input", val);
  }

  // ─── Computed ───────────────────────────────────────────────────────────────

  get employeesWithSchedules(): EmployeeRow[] {
    const today = moment().startOf("day");
    return this.employees
      .map(emp => ({
        employeeId: emp.id,
        fullName: `${emp.firstName} ${emp.lastName}`,
        profilePicture: emp.profilePicture as string | undefined,
        schedules: this.schedules.filter(
          s => s.assignee === emp.id && !moment(s.date).isBefore(today),
        ),
      }))
      .filter(e => e.schedules.length > 0);
  }

  get filteredEmployees(): EmployeeRow[] {
    if (!this.search) return this.employeesWithSchedules;
    const q = this.search.toLowerCase();
    return this.employeesWithSchedules.filter(e =>
      e.fullName.toLowerCase().includes(q),
    );
  }

  get totalSelectedSchedules(): number {
    return Object.values(this.selectedSchedules).reduce(
      (acc, set) => acc + set.size,
      0,
    );
  }

  get selectedEmployeeCount(): number {
    return Object.values(this.selectedSchedules).filter(s => s.size > 0).length;
  }

  get globalAllSelected(): boolean {
    return (
      this.employeesWithSchedules.length > 0 &&
      this.employeesWithSchedules.every(e => this.employeeAllSelected(e))
    );
  }

  get globalSomeSelected(): boolean {
    return this.totalSelectedSchedules > 0;
  }

  // ─── Per-employee helpers ────────────────────────────────────────────────────

  employeeAllSelected(item: EmployeeRow): boolean {
    const set = this.selectedSchedules[item.employeeId];
    return !!set && set.size === item.schedules.length;
  }

  employeeSelectedCount(item: EmployeeRow): number {
    return this.selectedSchedules[item.employeeId]?.size ?? 0;
  }

  isScheduleSelected(employeeId: number, scheduleId: number): boolean {
    return this.selectedSchedules[employeeId]?.has(scheduleId) ?? false;
  }

  // ─── Toggle helpers ──────────────────────────────────────────────────────────

  toggleGlobalAll(val: boolean) {
    const map: SelectionMap = {};
    if (val) {
      this.employeesWithSchedules.forEach(e => {
        map[e.employeeId] = new Set(e.schedules.map(s => s.id));
      });
    } else {
      this.employeesWithSchedules.forEach(e => {
        map[e.employeeId] = new Set();
      });
    }
    this.selectedSchedules = map;
  }

  toggleAllEmployeeSchedules(item: EmployeeRow) {
    const newMap = { ...this.selectedSchedules };
    if (this.employeeAllSelected(item)) {
      // Deselect all
      newMap[item.employeeId] = new Set();
    } else {
      // Select all
      newMap[item.employeeId] = new Set(item.schedules.map(s => s.id));
    }
    this.selectedSchedules = newMap;
  }

  toggleSchedule(employeeId: number, scheduleId: number) {
    const newMap = { ...this.selectedSchedules };
    const set = new Set(newMap[employeeId] ?? []);
    if (set.has(scheduleId)) {
      set.delete(scheduleId);
    } else {
      set.add(scheduleId);
    }
    newMap[employeeId] = set;
    this.selectedSchedules = newMap;
  }

  toggleExpand(employeeId: number) {
    const idx = this.expandedIds.indexOf(employeeId);
    if (idx === -1) {
      this.expandedIds.push(employeeId);
    } else {
      this.expandedIds.splice(idx, 1);
    }
  }

  // ─── Formatting ─────────────────────────────────────────────────────────────

  initials(name: string): string {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  formatDay(date: string): string {
    return moment(date).format("D");
  }

  formatMonth(date: string): string {
    return moment(date).format("MMM");
  }

  formatTime(time: string): string {
    if (!time) return "—";
    return moment(time, ["HH:mm:ss", "HH:mm"]).format("h:mma");
  }

  statusColor(status: string): string {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "green darken-1";
      case "OPEN":
        return "orange darken-1";
      case "PENDING":
        return "blue darken-1";
      default:
        return "grey";
    }
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  initSelection() {
    const map: SelectionMap = {};
    this.employeesWithSchedules.forEach(e => {
      map[e.employeeId] = new Set(e.schedules.map(s => s.id));
    });
    this.selectedSchedules = map;
    // Auto-expand all employees on open
    this.expandedIds = this.employeesWithSchedules.map(e => e.employeeId);
  }

  // ─── Send ────────────────────────────────────────────────────────────────────

  async sendSMS() {
    if (this.totalSelectedSchedules === 0) return;
    this.sending = true;
    try {
      const payload = this.employeesWithSchedules
        .map(e => ({
          employeeId: e.employeeId,
          schedules: e.schedules.filter(s =>
            this.selectedSchedules[e.employeeId]?.has(s.id),
          ),
        }))
        .filter(e => e.schedules.length > 0);

      const response = (await this.$store.dispatch(
        "schedule/sendScheduleSMS",
        payload,
      )) as SendScheduleSMSResponse;

      const errors = response?.messageErrors || [];
      const failureCount = errors.length;
      const successCount =
        typeof response?.success === "number"
          ? response.success
          : Math.max(0, payload.length - failureCount);

      this.results = {
        successCount,
        failureCount,
        errors,
      };

      if (failureCount === 0) {
        this.$store.commit(
          "alert/SET_SHOW_SUCCESS",
          "Schedule SMS sent successfully!",
          { root: true },
        );
      } else {
        this.$store.commit(
          "alert/SET_SHOW_SUCCESS",
          `Sent schedule SMS with ${failureCount} failure(s).`,
          { root: true },
        );
      }
    } catch {
      // errors handled in store action
    } finally {
      this.sending = false;
    }
  }

  close() {
    this.internalValue = false;
    this.search = "";
    this.expandedIds = [];
    this.selectedSchedules = {};
    this.results = null;
  }
}
